import { Component, Inject, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { Router } from "@angular/router";
import { IdArray, IdObject } from "@app/_classes/cosmos-entity";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { logger } from "@app/_helpers/logger";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { hasPermissionByKey, isSupervisor, useAdmin } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import {
	createRxValue,
	DEFAULT_PERMISSION_GROUPS,
	distinctUntilChangedJson,
	fromRxValue,
	getActiveSchedule,
	mapUsersFromIds,
	WithOptional,
} from "@app/_helpers/utils";
import { CustomValidators } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import { AppStateService } from "@app/app-states.service";
import { ConfirmDialogComponent } from "@app/components/generic-dialogs/confirm-dialog/confirm-dialog.component";
import { createTargetHourGroup } from "@app/components/target-hours-control/target-hours-control.component";
import { validateOverlapItems } from "@app/components/target-hours-control/validators";
import {
	CreditDeductDialogComponent,
	CreditDeductDialogData,
} from "@app/components/timecontingent/credit-deduct-dialog/credit-deduct-dialog.component";
import { CreditDeductItem } from "@app/components/timecontingent/credit-deduct-dialog/credit-deduct-types";
import { HttpService } from "@app/core";
import { CurrentContingent } from "@app/pages/reports-page/contingent-page/contingent-page.component";
import { MatRightSideSheet } from "@app/shared/mat-right-side-sheet/mat-right-side-sheet";
import { TimeDatePickerComponent } from "@app/shared/time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "@app/shared/time-date-picker/time-date-picker-config";
import { WorkspaceUsersLimitationsWarnDialogComponent } from "@app/shared/workspace-users-limitations-warn-dialog/workspace-users-limitations-warn-dialog.component";
import { environment } from "@env/environment";
import { UntilDestroy } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	Logger,
	NotifyService,
	UserSettingsQuery,
	Workspace,
	WorkspacesQuery,
	WorkspacesService,
	WorkspaceUserSchedule,
} from "@timeghost/web-api";
import { isSameDay, isValid, startOfDay, startOfToday } from "date-fns/esm";
import produce from "immer";
import { DateTime } from "luxon";
import { combineLatest, defer, filter, firstValueFrom, map, startWith, switchMap } from "rxjs";
import { firstBy } from "thenby";
export type UserSupervisorMap = IdArray;
export type UserSupervisor = UserSupervisorMap[0];
const log = new Logger("Workspace/UpdateUser");
const DEDUCT_TYPES = ["deduct"];
@UntilDestroy()
@Component({
	selector: "tg-update-user-dialog",
	templateUrl: "./update-user-dialog.component.html",
	styleUrls: ["./update-user-dialog.component.scss"],
})
export class WorkspaceUserDialogComponent implements OnInit {
	readonly selectedTabIndex = createRxValue(0);
	readonly group = new FormGroup({
		name: new FormControl({
			value: "",
			disabled: true,
		}),
		email: new FormControl({
			value: "",
			disabled: true,
		}),
		labor_cost: new FormControl<number>(null, [CustomValidators.isFloat]),
		billable_rate: new FormControl<number>(null, [CustomValidators.isFloat]),
		staff_number: new FormControl<string>(null, [Validators.maxLength(191)]),
		employment_date: new FormControl<Date>(null),
		has_license: new FormControl<boolean>(false),
		schedules: new FormArray(
			[],
			[
				(ctrl) => {
					if (!ctrl.value) return null;
					for (const sched of ctrl.value) {
						const overlapSameDayError = ctrl.value.find(
							(x: any) => x !== sched && isSameDay(new Date(x.from), new Date(sched.from))
						);
						if (overlapSameDayError) {
							return {
								overlapSameDayError,
							};
						}
						for (const day of sched.items) {
							const result = validateOverlapItems()({ value: day } as any);
							if (result) return result;
						}
					}
					return null;
				},
			]
		),
		supervisors: new FormControl<UserSupervisorMap>([]),
		subordinates: new FormControl<UserSupervisorMap>([]),
		groups: new FormControl<IdArray>([]),
		credits: new FormControl<CreditDeductItem[]>([]),
	});
	get groupValue() {
		return this.group.getRawValue();
	}
	readonly user = createRxValue<WorkspaceUser>(
		this.userSettingsQuery.getValue().workspace.users.find((x) => x.id === this.data.userId)
	);
	readonly scheduleEnabled$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => !!x?.workspace?.schedules?.workspace?.enabled));
	readonly workspaceStatus$ = this.userSettingsQuery.select().pipe(
		map((user) => {
			const status = parseSubscriptionAsStatus(user.workspace, user);
			const wsUsers = user.workspace.users;
			const activeUsers = wsUsers.filter((x) => !!x.has_license && !x.removed);
			const maxUsers = status.subscription.quantity;
			const qu = (100 / maxUsers) * activeUsers.length;
			const quota = { percentage: qu, max: maxUsers, current: activeUsers.length };
			return { ...status, isUserSlotsExhausted: status.isTrial ? false : quota.current >= quota.max, quota };
		})
	);
	constructor(
		@Inject(MAT_DIALOG_DATA) private data: { userId: string; selectedTabIndex?: number },
		private ref: MatDialogRef<WorkspaceUserDialogComponent>,
		private userSettingsQuery: UserSettingsQuery,
		private workspaceQuery: WorkspacesQuery,
		private workspaceService: WorkspacesService,
		private http: HttpService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private sheet: MatRightSideSheet,
		private appService: AppService,
		private appState: AppStateService,
		private notifyService: NotifyService,
		private router: Router
	) {
		if (typeof this.data.selectedTabIndex === "number") {
			this.selectedTabIndex.next(this.data.selectedTabIndex);
		}
	}
	readonly projecthoursEnabled$ = this.appService.projecthoursEnabled$;
	readonly comegoEnabled$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => !!x.workspace?.settings?.workinghoursEnabled));
	readonly workspaceSchedule = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => x.workspace.schedules.workspace))
	);
	readonly timecontingent$ = fromRxValue(
		this.group.valueChanges.pipe(
			map(() => this.group.getRawValue()),
			map(({ credits }) => {
				const userSettings = this.userSettingsQuery.getValue();
				if (!credits) return [];
				return credits.reduce(
					(acc, r) => {
						const [refUser] = mapUsersFromIds(userSettings.workspace.users, [r.user]);
						if (refUser) acc.push({ ...r, user: refUser, date: new Date(r.date) });
						return acc;
					},
					[] as (Omit<CreditDeductItem, "user"> & { user: WorkspaceUser })[]
				);
			})
		)
	);
	readonly contingentVisible$ = this.userSettingsQuery.select().pipe(
		map((user) => {
			if (!user) return false;
			if (!user.workspace.settings?.["workinghoursEnabled"]) return false;
			if (!user.workspace?.["timecontingent"]?.["enabled"]) return false;
			return (
				hasPermissionByKey(user, "groupsCanContingentViewSelf") ||
				(user.workspace["timecontingent"]?.["superviseCanViewSubordinates"] && isSupervisor(user))
			);
		})
	);
	readonly timecontingent$timesLoading = createRxValue(false);
	readonly timecontingent$values = fromRxValue(
		combineLatest([
			this.notifyService.onMessage.pipe(
				startWith({
					payload: {
						type: "timecontingent.calculate",
						state: "completed",
					},
				}),
				filter(
					(message) =>
						message.payload.type === "timecontingent.calculate" && message.payload.state === "completed"
				)
			),
			this.userSettingsQuery.select().pipe(
				map((x) => ({
					timecontingent: x.workspace?.["timecontingent"]?.users?.[this.data.userId],
					user: x.workspace?.users?.find((u) => u.id === this.data.userId),
					workspace: x.workspace,
				})),
				map(({ user, timecontingent, workspace }) => ({
					employmentDate: user?.["employment_date"],
					timecontingent,
					workspaceChangeTs: workspace?.["_ts"] ?? 0,
				})),
				distinctUntilChangedJson()
			),
		]).pipe(
			distinctUntilChangedJson(),
			debounceTimeAfterFirst(1000),
			switchMap(async ([message, { employmentDate }]) => {
				if (!employmentDate) {
					this.timecontingent$timesLoading.next(false);
					return [];
				}
				this.timecontingent$timesLoading.next(true);
				return await this.http
					.post<CurrentContingent[]>(environment.serverUrl + "/currentContingent", {
						includeToday: false,
						users: [this.userId],
					})
					.toPromise()
					.finally(() => {
						this.timecontingent$timesLoading.next(false);
					});
			})
		)
	);
	readonly timecontingent$times = fromRxValue(
		combineLatest([this.timecontingent$.asObservable(), this.timecontingent$values.asObservable()]).pipe(
			filter(([credits, values]) => !!credits && !!values),
			switchMap(async ([credits, values]) => {
				const userSettings = this.userSettingsQuery.getValue();
				const employmentDate = ((d) => d && new Date(d))(
					userSettings.workspace.users.find((d) => d.id === this.userId)?.["employment_date"]
				);
				const { contingent, ...returnValue } = values.reduce(
					(acc, { actualHours, balance }) => {
						acc.workinghours += balance;
						acc.actualHours += actualHours;
						acc.contingent += balance;
						return acc;
					},
					{
						actualHours: 0,
						workinghours: 0,
						credit:
							credits?.reduce((wcc, w) => {
								if (w.type === "deduct") return (wcc -= w.value);
								return (wcc += w.value);
							}, 0) ?? 0,
						contingent: 0,
					}
				);
				this.isLoading.update(false);
				return {
					...returnValue,
					enabled: employmentDate && isValid(new Date(employmentDate)),
					contingent: returnValue.credit + contingent,
					credits,
				};
			})
		)
	);
	refreshControls(workspace: Workspace) {
		const foundUser = workspace.users.find((x) => x.id === this.data.userId);
		const user = (this.user.value = {
			...foundUser,
			isSynced: !!foundUser?.["memberOfSynchronizedGroups"]?.find((s: any) => s.id !== foundUser.id),
		} as WorkspaceUser);
		const employmentDate = user?.["employment_date"] && new Date(user?.["employment_date"]);
		const groupData: typeof this.group.value = {
			name: user?.name,
			email: user?.email,
			labor_cost: user?.labor_cost,
			billable_rate: user?.billable_rate,
			employment_date: (employmentDate && isValid(employmentDate) && employmentDate) || null,
			staff_number: user?.staff_number,
			has_license: user?.has_license ?? false,
			supervisors: user?.["supervisors"] ?? [],
			subordinates: workspace.users.reduce((acc, u) => {
				if (u.id === user.id) return acc;
				let supervisor: IdObject;
				if ((supervisor = u["supervisors"]?.find((x: IdObject) => x.id === user.id))) {
					acc.push({ id: u.id });
				}
				return acc;
			}, [] as IdArray),
			groups: this.getCustomGroupIdsForUser(workspace, user.id),
			credits: workspace["timecontingent"]?.users?.[user.id]?.credit ?? [],
		};
		this.group.patchValue(
			{
				...groupData,
			},
			{ emitEvent: false }
		);
		this.group.controls.schedules.clear({ emitEvent: false });
		(workspace.schedules?.users[user.id] || [])
			.map((d) => ({ ...d, showItems: false, editing: false }))
			.sort(firstBy((d: any) => new Date(d.from), "desc").thenBy((d) => d.enabled, "desc"))
			.reduce((acc, r) => {
				acc.push(
					new FormControl<WithOptional<WorkspaceUserSchedule>>({
						...r,
						items:
							r.items?.map(({ items, ...d }) => {
								return {
									...d,
									items:
										items?.map((dt, i) => ({
											...dt,
											name: dt.name || "",
											day: i,
										})) || [],
								};
							}) || [],
					})
				);
				return acc;
			}, this.group.controls.schedules);
		this.group.updateValueAndValidity();
		this.group.setMustChange();
		this.group.markAsPristine();
	}
	refreshTest() {
		return this.refreshControls(this.userSettingsQuery.getValue().workspace);
	}
	removeSupervisor(sid: string) {
		const { supervisors: users } = this.group.getRawValue();
		const idx = users.findIndex((d) => d.id === sid);
		if (idx === -1) return;
		this.group.patchValue({
			supervisors: users.filter((d) => d.id !== sid),
		});
		this.group.markAsDirty();
	}
	removeSubordinate(sid: string) {
		const { subordinates: users } = this.group.getRawValue();
		const idx = users.findIndex((d) => d.id === sid);
		if (idx === -1) return;
		this.group.patchValue({
			subordinates: users.filter((d) => d.id !== sid),
		});
		this.group.markAsDirty();
	}
	removeGroup(groupId: string) {
		const { groups } = this.group.getRawValue();
		const idx = groups.findIndex((d) => d.id === groupId);
		if (idx === -1) return;
		this.group.patchValue({
			groups: groups.filter((d) => d.id !== groupId),
		});
		this.group.markAsDirty();
	}
	readonly currency$ = this.userSettingsQuery.select((x) => x.workspace?.settings?.currency || "$");
	hasAdmin() {
		const user = this.userSettingsQuery.getValue();
		return !!user.workspace.users.find((x) => x.admin && x.id === user.id) || this.data.userId === user.id;
	}
	readonly isAdmin$ = useAdmin();
	get userId() {
		return this.user.value?.id;
	}
	get isCurrentUser() {
		return this.userSettingsQuery.getValue().id === this.userId;
	}
	async checkBeforeClose() {
		if (this.isLoading.value) return false;
		if (this.group.dirty) {
			const discardChanges = await toPromise(
				this.dialog
					.open(ConfirmDialogComponent, {
						data: {
							text: this.translate.instant("utils.unsaved-changes"),
						},
					})
					.afterClosed()
			);
			if (!discardChanges) return false;
		}
		if (this.group.pristine) this.ref.close();
		else this.refreshTest();
	}
	ngOnInit(): void {
		this.ref.updateSize("680px");
		this.refreshControls(this.userSettingsQuery.getValue().workspace);
		this.ref.disableClose = true;
		this.ref.backdropClick().subscribe(() => {
			this.checkBeforeClose();
		});
		this.ref.keydownEvents().subscribe((k) => k.key === "Escape" && this.checkBeforeClose());
		logger.debug("group", this.group, this.group.pristine);
	}
	readonly isLoading = createRxValue(false);
	readonly timecontingentState = this.appState.timecontingentCalculateState.asObservable().pipe(
		filter((data) => !data || data.users?.includes?.(this.userId)),
		map((d) => d?.state === "processing" && d)
	);
	waitForEmploymentUpdate() {
		const values = this.group.getRawValue();
		if (!values.employment_date) return Promise.resolve(true);
		return new Promise<boolean>((resolve) => {
			this.notifyService.onMessage
				.asObservable()
				.pipe(filter((s) => s.payload?.type === "timecontingent.calculate" && s.payload.state === "completed"))
				.subscribe({ next: () => resolve(true), error: () => resolve(false) });
		});
	}
	saveChanges() {
		const ws = this.workspaceQuery.getEntity(this.userSettingsQuery.getValue().workspace.id) as Workspace;
		const {
			subordinates: newSubordinates,
			credits,
			has_license,
			groups: newGroups,
			...value
		} = this.group.getRawValue();
		const currentSubordinates = ws.users.filter((d) => d["supervisors"]?.find((u: any) => u.id === this.userId));
		const userIndex = ws.users.findIndex((x) => x.id === this.userId);
		if (userIndex === -1) return;
		const currentUser = ws.users[userIndex];
		const defaultGroupIds = Object.values(DEFAULT_PERMISSION_GROUPS);
		const currentGroupIds = (ws.groups || [])
			.filter((group) => group.id && !defaultGroupIds.includes(group.id))
			.filter((group) => group.users?.some((groupUser) => groupUser.id === this.userId))
			.map((group) => group.id);
		const selectedGroupIds = (newGroups || []).map((group) => group.id);
		const addedGroupIds = selectedGroupIds.filter((id) => !currentGroupIds.includes(id));
		const removedGroupIds = currentGroupIds.filter((id) => !selectedGroupIds.includes(id));
		const employmentDate = new Date(value.employment_date);
		const previousEmploymentDate = currentUser?.["employment_date"] && new Date(currentUser["employment_date"]);
		const employmentDateChanged =
			!!value.employment_date &&
			isValid(employmentDate) &&
			!(
				previousEmploymentDate &&
				isValid(previousEmploymentDate) &&
				isSameDay(employmentDate, previousEmploymentDate)
			);
		const waitForEmploymentUpdate =
			employmentDateChanged &&
			currentUser.has_license &&
			!!ws["timecontingent"]?.["enabled"] &&
			this.waitForEmploymentUpdate();
		const creditsDirty = this.group.controls.credits.dirty;

		return firstValueFrom(
			defer(() => {
				this.isLoading.update(true);
				this.group.disable();
				return this.workspaceService.update(
					produce(ws, (draft) => {
						// remove all current subordinates
						currentSubordinates.forEach(({ id: sid }) => {
							const subordinate = draft.users.find((d) => d.id === sid);
							if (!subordinate) return;
							// @ts-ignore
							subordinate.supervisors =
								// @ts-ignore
								subordinate.supervisors?.filter((d) => d.id !== currentUser.id) ?? [];
						});
						// add new selection of  subordinates
						if (newSubordinates?.length) {
							newSubordinates.forEach(({ id: sid }) => {
								const subordinate = draft.users.find((d) => d.id === sid);
								if (!subordinate) return;
								// @ts-ignore
								subordinate.supervisors = (
									(subordinate["supervisors"] as IdObject[])?.filter(
										(d) => d.id !== currentUser.id
									) ?? []
								).concat({
									id: currentUser.id,
								});
							});
						}
						draft.users[userIndex] = {
							...draft.users[userIndex],
							labor_cost: value.labor_cost,
							billable_rate: value.billable_rate,
							staff_number: value.staff_number,
							has_license: has_license,
							// @ts-ignore - todo add typings
							supervisors: value.supervisors ?? [],
							// @ts-ignore - todo add typings
							...(value.employment_date && isValid(employmentDate)
								? {
										employment_date: DateTime.fromJSDate(employmentDate).toISODate(),
									}
								: value.employment_date === null
									? { employment_date: null }
									: {}),
						};
						if (!draft.schedules)
							draft.schedules = {
								users: {},
								workspace: { enabled: false, items: [] },
							};
						draft.schedules.users[this.userId] = (value.schedules || [])
							.filter(Boolean)
							.map(({ editing, showItems, from, ...d }: any) => ({
								...d,
								from: !from ? startOfToday().toISOString() : startOfDay(new Date(from)).toISOString(),
								items: d.items?.map(({ editing, ...ud }: any) => ud) || [],
							})) as WorkspaceUserSchedule[];
						if (creditsDirty) {
							if (!draft["timecontingent"]) draft["timecontingent"] = {};
							if (!draft["timecontingent"].users) draft["timecontingent"].users = {};
							if (!draft["timecontingent"].users[this.userId])
								draft["timecontingent"].users[this.userId] = { credit: [] };
							draft["timecontingent"].users[this.userId].credit = credits.map(
								({ user: creditAuthor, ...c }: any) => ({
									...c,
									user: creditAuthor?.id ?? creditAuthor,
								})
							);
						} else {
							delete draft["timecontingent"];
						}
						if (addedGroupIds.length || removedGroupIds.length) {
							for (const groupId of addedGroupIds) {
								const group = draft.groups?.find((g) => g.id === groupId);
								if (group && !group.users?.some((u) => u.id === currentUser.id)) {
									group.users = [
										...(group.users || []),
										{ id: currentUser.id, name: currentUser.name },
									];
								}
							}
							for (const groupId of removedGroupIds) {
								const group = draft.groups?.find((g) => g.id === groupId);
								if (group) {
									group.users = group.users?.filter((u) => u.id !== currentUser.id) || [];
								}
							}
						}
						return draft;
					})
				);
			})
		)
			.then((w) => {
				if (waitForEmploymentUpdate) {
					this.timecontingent$timesLoading.next(true);
					return waitForEmploymentUpdate.then(() => w);
				}
				return w;
			})
			.then((x) => {
				this.group.enable();
				this.refreshControls(x);
				this.group.updateValueAndValidity();
				this.appService.notifySuccess("success.changesSaved");
			})
			.catch((err) => {
				this.group.enable();
				this.group.updateValueAndValidity();
				pushError(err);
			})
			.finally(() => {
				this.isLoading.update(false);
				if (employmentDateChanged) this.group.controls.employment_date.markAsPristine();
			});
	}
	addSchedule() {
		const from = new Date();
		const user = this.userSettingsQuery.getValue();
		const workspaceSchedule = getActiveSchedule({ ...user, ...this.user.value }, from);
		this.group.controls.schedules.insert(
			0,
			new FormControl({
				from,
				items:
					(workspaceSchedule?.enabled ? workspaceSchedule?.items : null) ||
					(createTargetHourGroup().value.items! as any),
				enabled: true,
				editing: false,
				name: this.translate.instant("schedules.defaultByUsername", {
					name: this.user.value.name,
				}),
			})
		);
		this.group.markAsDirty();
		this.group.updateValueAndValidity();
	}
	isCurrentSchedule(index: number) {
		const scheds: any[] = this.group.value?.schedules;
		return scheds?.length && !scheds.slice(0, index).find((x) => x.enabled);
	}
	copySchedule(index: number) {
		const sched = structuredClone(this.group.value.schedules[index]);
		this.group.controls.schedules.insert(
			0,
			new FormControl({
				...sched,
				from: new Date(),
				enabled: true,
				editing: true,
				name: this.translate.instant("schedules.defaultByUsername", {
					name: this.user.value.name,
				}),
			})
		);
		this.group.markAsDirty();
		this.group.updateValueAndValidity();
	}
	async removeSchedule(index: number) {
		const isRemoval = await toPromise(
			this.dialog
				.open(ConfirmDialogComponent, {
					data: {
						text: this.translate.instant("schedules.delete-notice"),
						strings: {
							confirm: this.translate.instant("utils.delete-anyway"),
							cancel: this.translate.instant("utils.discard-change.cancel"),
						},
					},
					width: "460px",
				})
				.afterClosed()
		);
		if (isRemoval) {
			this.group.controls.schedules.removeAt(index);
			this.group.markAsDirty();
		}
	}
	get isWorkspaceInheritance() {
		const user = this.userSettingsQuery.getValue();
		const destUser = this.user.value;
		return (
			!user.workspace?.schedules.users?.[destUser.id]?.find((x) => x.enabled) &&
			user.workspace.schedules?.workspace?.enabled
		);
	}
	popoverSupervisorIds: string[] = [];

	openSupervisorFilterPopover(event: Event, popover: { toggle(): void }) {
		event.stopPropagation();
		if (this.group.disabled || this.isLoading.value) return;
		(event.currentTarget as HTMLButtonElement)?.blur();
		this.popoverSupervisorIds = (this.group.controls.supervisors.value || []).map((u) => u.id);
		popover.toggle();
	}

	onSupervisorFilterClose(userIds: string[]) {
		if (!userIds) return;
		const currentIds = new Set((this.group.controls.supervisors.value || []).map((u) => u.id));
		const hasChanges = userIds.length !== currentIds.size || userIds.some((id) => !currentIds.has(id));
		if (!hasChanges) return;
		this.group.patchValue({ supervisors: userIds.map((id) => ({ id })) });
		this.group.markAsDirty();
	}

	popoverSubordinateIds: string[] = [];

	openSubordinateFilterPopover(event: Event, popover: { toggle(): void }) {
		event.stopPropagation();
		if (this.group.disabled || this.isLoading.value) return;
		(event.currentTarget as HTMLButtonElement)?.blur();
		this.popoverSubordinateIds = (this.group.controls.subordinates.value || []).map((u) => u.id);
		popover.toggle();
	}

	onSubordinateFilterClose(userIds: string[]) {
		if (!userIds) return;
		const currentIds = new Set((this.group.controls.subordinates.value || []).map((u) => u.id));
		const hasChanges = userIds.length !== currentIds.size || userIds.some((id) => !currentIds.has(id));
		if (!hasChanges) return;
		this.group.patchValue({ subordinates: userIds.map((id) => ({ id })) });
		this.group.markAsDirty();
	}
	async openCreditDialog(value: any, $index: number) {
		if (this.isLoading.value) return;
		const user = this.userSettingsQuery.getValue().workspace.users?.find((d) => d.id === this.userId);
		return await this.sheet
			.open(CreditDeductDialogComponent, {
				data: { user, value, disabledFields: { user: !!user } } as CreditDeductDialogData,
			})
			.afterClosed()
			.toPromise()
			.then((credit) => {
				if (!credit) return;
				let credits = [...(this.group.getRawValue()?.credits ?? [])];
				if (credit.deleted) credits.splice($index, 1);
				else if (typeof $index === "number") credits.splice($index, 1, credit);
				else credits.insert(0, credit);
				this.group.patchValue({ credits });
				this.group.controls.credits.markAsDirty();
			});
	}
	openCalPicker() {
		return this.sheet
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: this.groupValue.employment_date && new Date(this.groupValue.employment_date),
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.group.patchValue({
					employment_date: x,
				});
			});
	}
	private getCustomGroupIdsForUser(workspace: Workspace, userId: string): IdArray {
		const defaultGroupIds = Object.values(DEFAULT_PERMISSION_GROUPS);

		return (workspace.groups || [])
			.filter((group) => group.id && !defaultGroupIds.includes(group.id))
			.filter((group) => group.users?.some((groupUser) => groupUser.id === userId))
			.map(({ id }) => ({ id }));
	}
	getUserGroupEntities(): { id: string; name: string }[] {
		const workspace = this.userSettingsQuery.getValue().workspace;

		return (this.group.controls.groups.value || [])
			.map(({ id }) => workspace.groups?.find((group) => group.id === id))
			.filter(Boolean)
			.map(({ id, name }) => ({ id, name }))
			.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
	}
	popoverGroupIds: string[] = [];

	openGroupFilterPopover(event: Event, popover: { toggle(): void }) {
		event.stopPropagation();
		if (this.group.disabled || this.isLoading.value) return;
		(event.currentTarget as HTMLButtonElement)?.blur();
		this.popoverGroupIds = (this.group.controls.groups.value || []).map((g) => g.id);
		popover.toggle();
	}

	onGroupFilterClose(groupIds: string[]) {
		if (!groupIds) return;
		const currentIds = new Set((this.group.controls.groups.value || []).map((g) => g.id));
		const hasChanges = groupIds.length !== currentIds.size || groupIds.some((id) => !currentIds.has(id));
		if (!hasChanges) return;
		this.group.patchValue({ groups: groupIds.map((id) => ({ id })) });
		this.group.markAsDirty();
	}

	async onLicenseToggle(event: any) {
		const currentFormValue = this.group.controls.has_license.value;
		const newValue = !currentFormValue; // Toggle the current form value
		const currentUser = this.user.value;
		if (!currentUser) return;

		// If trying to enable license when it's currently disabled
		if (newValue && !currentUser.has_license) {
			const ws = this.workspaceQuery.getEntity(this.userSettingsQuery.getValue().workspace.id);
			const status = await firstValueFrom(this.workspaceStatus$);

			if (!status.isTrial) {
				const currentLicensedUsers = ws.users.filter((x) => !x.removed && x.has_license).length;
				if (currentLicensedUsers >= status.quota.max) {
					// Prevent the toggle from changing
					event.source.checked = currentFormValue;

					// Show warning dialog
					this.dialog.open(WorkspaceUsersLimitationsWarnDialogComponent, {
						data: {
							upgradeUrl: this.router.createUrlTree(["/settings/workspace", "subscriptions"]),
							onUpgrade: () => {
								this.ref.close();
							},
						},
					});
					return;
				}
			}
		}

		// Update the form control if validation passed
		this.group.controls.has_license.setValue(newValue);
	}

	async removeUser(user: WorkspaceUser) {
		const confirmed = await toPromise(
			this.dialog
				.open(ConfirmDialogComponent, {
					data: {
						text: this.translate.instant("utils.delete-user"),
						strings: {
							confirm: this.translate.instant("utils.remove"),
							cancel: this.translate.instant("utils.cancel"),
						},
					},
					width: "400px",
				})
				.afterClosed()
		);

		if (!confirmed) return;

		const ws = this.workspaceQuery.getEntity(this.userSettingsQuery.getValue().workspace.id);
		this.isLoading.update(true);
		this.group.disable();

		return firstValueFrom(
			defer(() => {
				return this.workspaceService.update(
					produce(ws, (draft) => {
						const userIndex = draft.users.findIndex((x) => x.id === user.id);
						if (userIndex !== -1) {
							draft.users[userIndex].removed = true;
							draft.users[userIndex].has_license = false;
							if (draft.users[userIndex].admin) {
								draft.users[userIndex].admin = draft.users.filter((x) => x.admin).length > 1;
							}
						}
						delete draft["timecontingent"];
					})
				);
			})
		)
			.then((x) => {
				this.group.enable();
				this.refreshControls(x);
				this.group.updateValueAndValidity();
				this.appService.notifySuccess("user.removed", undefined, { name: user.name });
				// Close the dialog after deactivation
				this.ref.close();
			})
			.catch((err) => {
				this.group.enable();
				this.group.updateValueAndValidity();
				pushError(err);
			})
			.finally(() => {
				this.isLoading.update(false);
			});
	}
}
interface WorkspaceUser {
	admin: boolean;
	billable_rate: number;
	email: string;
	has_license?: boolean;
	id: string;
	isSynced?: boolean;
	labor_cost: number;
	memberOfSynchronizedGroups?: Array<{ id: string; type: string }>;
	name: string;
	removed?: boolean;
	staff_number: string;
}
